/**
 * Copy text to the system clipboard with execCommand fallback for restricted contexts.
 */
import { logSignInLinkCopyWarn } from '@/lib/sign-in-link-copy-log';
import {
  isSignInLinkCopySourceError,
  SignInLinkCopySourceError,
} from '@/lib/sign-in-link-copy-errors';

export type CopyResult = {
  ok: boolean;
  method?: 'clipboard-item' | 'writeText' | 'execCommand';
  reason?: string;
};

function formatCopyError(error: unknown): string {
  if (error instanceof DOMException) {
    return `${error.name}: ${error.message}`;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return String(error);
}

function supportsClipboardItem(): boolean {
  return (
    typeof ClipboardItem !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    typeof navigator.clipboard?.write === 'function'
  );
}

function warnCopyFailure(
  scope: string,
  result: CopyResult,
  extra?: Record<string, unknown>
): void {
  logSignInLinkCopyWarn(scope, {
    method: result.method,
    reason: result.reason,
    ...extra,
  });
}

/**
 * Copy text with detailed result (method + failure reason).
 */
export async function copyTextToClipboardDetailed(text: string): Promise<CopyResult> {
  const value = text.trim();
  if (!value) {
    return { ok: false, reason: 'empty-text' };
  }

  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(value);
      return { ok: true, method: 'writeText' };
    } catch (error) {
      const reason = formatCopyError(error);
      // Fall through to legacy copy
      if (typeof document === 'undefined') {
        const result: CopyResult = { ok: false, method: 'writeText', reason };
        warnCopyFailure('clipboard writeText failed', result);
        return result;
      }
    }
  }

  if (typeof document === 'undefined') {
    const result: CopyResult = { ok: false, reason: 'no-document' };
    warnCopyFailure('clipboard unavailable', result);
    return result;
  }

  try {
    const textarea = document.createElement('textarea');
    textarea.value = value;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    textarea.style.top = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    const copied = document.execCommand('copy');
    document.body.removeChild(textarea);
    if (copied) {
      return { ok: true, method: 'execCommand' };
    }
    const result: CopyResult = { ok: false, method: 'execCommand', reason: 'execCommand-returned-false' };
    warnCopyFailure('clipboard execCommand failed', result);
    return result;
  } catch (error) {
    const result: CopyResult = { ok: false, method: 'execCommand', reason: formatCopyError(error) };
    warnCopyFailure('clipboard execCommand threw', result);
    return result;
  }
}

/** @deprecated Prefer copyTextToClipboardDetailed or copyTextFromPromise */
export async function copyTextToClipboard(text: string): Promise<boolean> {
  const result = await copyTextToClipboardDetailed(text);
  return result.ok;
}

/**
 * Copy text resolved from an async source while preserving the user-gesture window.
 * Uses ClipboardItem when available so clipboard registration happens before the network call finishes.
 */
export async function copyTextFromPromise(getText: () => Promise<string>): Promise<CopyResult> {
  const textPromise = getText();

  if (supportsClipboardItem()) {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/plain': textPromise.then((text) => {
            const value = text.trim();
            if (!value) {
              throw new SignInLinkCopySourceError('empty-link', 'No text to copy');
            }
            return new Blob([value], { type: 'text/plain' });
          }),
        }),
      ]);
      return { ok: true, method: 'clipboard-item' };
    } catch (error) {
      if (isSignInLinkCopySourceError(error)) {
        throw error;
      }
      // ClipboardItem failed (e.g. gesture expired in older browsers) — fall through using same promise
    }
  }

  let text: string;
  try {
    text = await textPromise;
  } catch (error) {
    if (isSignInLinkCopySourceError(error)) {
      throw error;
    }
    throw error;
  }

  const trimmed = text.trim();
  if (!trimmed) {
    throw new SignInLinkCopySourceError('empty-link', 'No text to copy');
  }

  const result = await copyTextToClipboardDetailed(trimmed);
  if (!result.ok) {
    warnCopyFailure('copy blocked after fallback', result);
  }
  return result;
}
